import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, AlertTriangle, Users, HeartHandshake, BarChart3,
  Search, Bell, Plus, Menu, X, Activity, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AddStudentDialog from "@/components/AddStudentDialog";
import { api } from "@/api";
import { initials, riskStyle } from "@/lib/risk";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/risk-queue", label: "Risk Queue", icon: AlertTriangle },
  { to: "/students", label: "Students", icon: Users },
  { to: "/interventions", label: "Interventions", icon: HeartHandshake },
  { to: "/insights", label: "Insights", icon: BarChart3 },
];

function SidebarContent({ onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-6 py-6">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-brand text-white shadow-lg">
          <Activity className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display text-lg font-bold leading-none text-ink">BatchPulse</div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-indigo-brand">AI</div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all ${
                isActive
                  ? "bg-indigo-brand text-white card-shadow"
                  : "text-ink-muted hover:bg-secondary hover:text-ink"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="m-3 rounded-2xl border border-border bg-white p-4 card-shadow">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ink font-display text-sm font-bold text-white">
            A
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-ink">Aditi</div>
            <div className="truncate text-xs text-ink-muted">BrightPath Academy</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [all, setAll] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.students().then(setAll).catch(() => {});
  }, []);

  const results = q.trim()
    ? all.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, 6)
    : [];

  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
      <Input
        data-testid="global-search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search students…"
        className="h-10 rounded-xl border-border bg-secondary/60 pl-9 text-sm"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-border bg-white card-shadow-lg">
          {results.map((s) => {
            const rs = riskStyle(s.risk_level);
            return (
              <button
                key={s.id}
                data-testid={`search-result-${s.id}`}
                onMouseDown={() => nav(`/students/${s.id}`)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-secondary"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-bold text-ink">
                  {initials(s.name)}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-ink">{s.name}</span>
                  <span className="block text-xs text-ink-muted">{s.grade} · {s.course}</span>
                </span>
                <span className="text-xs font-bold" style={{ color: rs.color }}>{s.risk_score}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [urgent, setUrgent] = useState([]);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.overview().then((d) => setUrgent(d.urgent || [])).catch(() => {});
  }, [loc.pathname]);

  return (
    <div className="flex min-h-screen bg-background grain">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-white/70 backdrop-blur-xl lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white card-shadow-lg animate-rise">
            <button
              className="absolute right-4 top-5 text-ink-muted"
              onClick={() => setMobileOpen(false)}
              data-testid="close-mobile-nav"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-white/80 px-4 py-3 backdrop-blur-xl sm:px-6">
          <button
            className="rounded-lg p-2 text-ink hover:bg-secondary lg:hidden"
            onClick={() => setMobileOpen(true)}
            data-testid="open-mobile-nav"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden flex-1 sm:block">
            <GlobalSearch />
          </div>
          <div className="flex-1 sm:hidden" />

          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className="relative rounded-xl border border-border bg-white p-2.5 text-ink hover:bg-secondary"
                data-testid="notifications-btn"
              >
                <Bell className="h-[18px] w-[18px]" />
                {urgent.length > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-risk-high text-[10px] font-bold text-white">
                    {urgent.length}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel>Retention alerts</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {urgent.length === 0 && (
                <div className="px-2 py-4 text-center text-sm text-ink-muted">No urgent alerts</div>
              )}
              {urgent.map((s) => (
                <DropdownMenuItem
                  key={s.id}
                  onClick={() => nav(`/students/${s.id}`)}
                  className="flex cursor-pointer items-start gap-2"
                  data-testid={`notif-${s.id}`}
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-risk-high" />
                  <span>
                    <span className="block text-sm font-semibold text-ink">{s.name} · risk {s.risk_score}</span>
                    <span className="block text-xs text-ink-muted">{s.primary_signal} — needs attention</span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-student-btn"
            className="h-10 gap-1.5 rounded-xl bg-indigo-brand font-semibold hover:bg-indigo-deep"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Student</span>
          </Button>
        </header>

        {/* Mobile search */}
        <div className="border-b border-border bg-white/60 px-4 py-2.5 sm:hidden">
          <GlobalSearch />
        </div>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>

      <AddStudentDialog open={addOpen} onOpenChange={setAddOpen} onCreated={(s) => nav(`/students/${s.id}`)} />
    </div>
  );
}
